import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { productCategoryLabelFromRow } from '../utils/productCategoryLabel';

/** Tanzania mobile (+2556… / +2557…); stored as 12 digits, display +255712345678 */
const TZ_MOBILE = /^255[67]\d{8}$/;

export function normalizeTanzaniaMobileDigits(input) {
  let d = String(input ?? '').replace(/\D/g, '');
  if (d.length === 10 && d[0] === '0' && (d[1] === '6' || d[1] === '7')) {
    d = `255${d.slice(2)}`;
  } else if (d.length === 9 && (d[0] === '6' || d[0] === '7')) {
    d = `255${d}`;
  }
  if (d.length === 12 && TZ_MOBILE.test(d)) return d;
  return null;
}

export function formatTanzaniaPhoneDisplay(digits12) {
  if (!digits12) return '';
  const s = String(digits12).replace(/\D/g, '');
  if (s.length === 12 && s.startsWith('255')) {
    return `+${s}`;
  }
  return String(digits12);
}

const LS_KEYS = {
  products: 'pos_products',
  stock: 'pos_stock',
  sales: 'pos_sales',
  expenses: 'pos_expenses',
  users: 'pos_users',
};

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function save(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/** Legacy `sku` values are merged into `barcode`; `sku` is dropped. */
function normalizeProduct(p) {
  if (!p || typeof p !== 'object') return p;
  const { sku, ...rest } = p;
  const merged = String(rest.barcode || sku || '').trim();
  return { ...rest, barcode: merged || undefined };
}

function normalizeProducts(list) {
  if (!Array.isArray(list)) return list;
  return list.map(normalizeProduct);
}

/** `email` was legacy login id; migrate to `phone` digits. Strip legacy `passwordHash`. */
function normalizeUser(u) {
  if (!u || typeof u !== 'object') return u;
  const phoneRaw = u.phone ?? u.email ?? '';
  const tz = normalizeTanzaniaMobileDigits(phoneRaw);
  const phone =
    tz ||
    (String(phoneRaw).replace(/\D/g, '') || undefined);
  const { email: _dropEmail, passwordHash: _dropPwd, ...rest } = u;
  return { ...rest, phone };
}

function normalizeUsers(list) {
  if (!Array.isArray(list)) return list;
  return list.map(normalizeUser);
}

/** No default users — first account is created on the setup screen. */
const seedUsers = [];

const seedSales = [];
const seedExpenses = [];

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const [products, setProducts] = useState(() => {
    const p = load(LS_KEYS.products, null);
    const list = p && p.length ? p : [];
    return normalizeProducts(list);
  });
  const [stockByProductId, setStockByProductId] = useState(() => {
    const s = load(LS_KEYS.stock, null);
    return s && Object.keys(s).length ? s : {};
  });
  const [sales, setSales] = useState(() => load(LS_KEYS.sales, seedSales));
  const [expenses, setExpenses] = useState(() =>
    load(LS_KEYS.expenses, seedExpenses)
  );
  const [users, setUsers] = useState(() => {
    const u = load(LS_KEYS.users, null);
    const list = u && u.length ? u : seedUsers;
    return normalizeUsers(list);
  });

  useEffect(() => {
    save(LS_KEYS.products, products);
  }, [products]);

  /** Stock rows only exist for products in the catalog — drop orphan keys, add missing ids at 0 */
  useEffect(() => {
    const ids = new Set(products.map((p) => p.id));
    setStockByProductId((prev) => {
      const next = {};
      for (const id of ids) {
        next[id] = prev[id] ?? 0;
      }
      return next;
    });
  }, [products]);

  useEffect(() => {
    save(LS_KEYS.stock, stockByProductId);
  }, [stockByProductId]);
  useEffect(() => {
    save(LS_KEYS.sales, sales);
  }, [sales]);
  useEffect(() => {
    save(LS_KEYS.expenses, expenses);
  }, [expenses]);
  useEffect(() => {
    save(LS_KEYS.users, users);
  }, [users]);

  const addProduct = useCallback((product) => {
    const fromServer =
      product?.id != null && String(product.id).trim() !== ''
        ? String(product.id).trim()
        : product?.serverProductId != null
          ? String(product.serverProductId).trim()
          : null;
    const id =
      fromServer ||
      (typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `p-${Date.now()}`);
    const barcode =
      typeof product.barcode === 'string' ? product.barcode.trim() : '';
    const { sku: _dropSku, ...rest } = product;
    setProducts((prev) => [
      ...prev,
      {
        ...rest,
        id,
        barcode: barcode || undefined,
        active: true,
      },
    ]);
    setStockByProductId((prev) => ({ ...prev, [id]: product.initialStock ?? 0 }));
  }, []);

  const updateProduct = useCallback((id, patch) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id ? normalizeProduct({ ...p, ...patch }) : p
      )
    );
  }, []);

  /** Replace catalog + stock from API `ilist` rows (same shape as server list). */
  const syncProductsFromServer = useCallback((apiRows) => {
    if (!Array.isArray(apiRows)) return;
    const mapped = apiRows.map((row) => ({
      id: String(row.id),
      /** Explicit numeric id for APIs (avoids ambiguity with string/number from storage). */
      serverProductId:
        row.id != null && !Number.isNaN(Number(row.id))
          ? Math.floor(Number(row.id))
          : undefined,
      name: String(row.name ?? ''),
      price: Math.max(0, Number(row.selling_price) || 0),
      barcode:
        row.barcode != null && String(row.barcode).trim() !== ''
          ? String(row.barcode).trim()
          : undefined,
      category: productCategoryLabelFromRow(row),
      active: row.can_be_sold !== 'no',
      serverProductUuid: row.uuid,
      categoryId: row.category_id,
    }));
    setProducts(normalizeProducts(mapped));
    setStockByProductId((prev) => {
      const ids = new Set(mapped.map((p) => p.id));
      const next = {};
      for (const id of ids) {
        const row = apiRows.find((r) => String(r.id) === id);
        const bal = row?.balance;
        next[id] =
          bal != null && !Number.isNaN(Number(bal))
            ? Number(bal)
            : (prev[id] ?? 0);
      }
      return next;
    });
  }, []);

  const deleteProduct = useCallback((id) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setStockByProductId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setStock = useCallback((productId, quantity) => {
    setStockByProductId((prev) => ({ ...prev, [productId]: Math.max(0, quantity) }));
  }, []);

  const adjustStock = useCallback((productId, delta) => {
    setStockByProductId((prev) => ({
      ...prev,
      [productId]: Math.max(0, (prev[productId] ?? 0) + delta),
    }));
  }, []);

  const checkout = useCallback(
    (cartLines, options) => {
      const opts =
        typeof options === 'string'
          ? { paymentMethod: options }
          : options || {};
      const paymentMethod = opts.paymentMethod ?? 'cash';
      const discountAmount = Math.max(0, Number(opts.discountAmount) || 0);
      const taxRatePercent = Math.max(
        0,
        Math.min(100, Number(opts.taxRatePercent) || 0)
      );

      for (const line of cartLines) {
        const available = stockByProductId[line.productId] ?? 0;
        if (line.qty > available) {
          return {
            ok: false,
            error: `Insufficient stock for product (need ${line.qty}, have ${available}).`,
          };
        }
      }

      const subtotal = cartLines.reduce(
        (sum, l) => sum + l.qty * l.unitPrice,
        0
      );
      const discount = Math.min(discountAmount, subtotal);
      const taxable = subtotal - discount;
      const tax = Math.round(taxable * (taxRatePercent / 100) * 100) / 100;
      const total = Math.round((taxable + tax) * 100) / 100;

      const saleId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s-${Date.now()}`;
      const sale = {
        id: saleId,
        lines: cartLines.map((l) => ({ ...l })),
        subtotal,
        discount,
        taxRate: taxRatePercent,
        tax,
        total,
        createdAt: new Date().toISOString(),
        paymentMethod,
      };
      setSales((prev) => [sale, ...prev]);
      setStockByProductId((prev) => {
        const next = { ...prev };
        for (const line of cartLines) {
          next[line.productId] = Math.max(
            0,
            (next[line.productId] ?? 0) - line.qty
          );
        }
        return next;
      });
      return { ok: true, sale };
    },
    [stockByProductId]
  );

  const addUser = useCallback((user) => {
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `u-${Date.now()}`;
    const row = normalizeUser({ ...user, id, active: true });
    setUsers((prev) => [...prev, row]);
    return id;
  }, []);

  const updateUser = useCallback((id, patch) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === id ? normalizeUser({ ...u, ...patch }) : u
      )
    );
  }, []);

  const deleteUser = useCallback((id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const addExpense = useCallback((expense) => {
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `e-${Date.now()}`;
    const price = Math.max(0, Number(expense.price) || 0);
    const row = {
      id,
      name: String(expense.name ?? '').trim(),
      category: String(expense.category ?? '').trim() || 'General',
      price,
      createdAt: new Date().toISOString(),
    };
    setExpenses((prev) => [row, ...prev]);
    return id;
  }, []);

  const updateExpense = useCallback((id, patch) => {
    setExpenses((prev) =>
      prev.map((e) => {
        if (e.id !== id) return e;
        const name = String(patch.name ?? e.name ?? '').trim();
        const category = String(patch.category ?? e.category ?? '').trim();
        const price = Math.max(0, Number(patch.price ?? e.price) || 0);
        return {
          ...e,
          name,
          category: category || 'General',
          price,
        };
      })
    );
  }, []);

  const deleteExpense = useCallback((id) => {
    setExpenses((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      products,
      stockByProductId,
      sales,
      expenses,
      users,
      addProduct,
      syncProductsFromServer,
      updateProduct,
      deleteProduct,
      setStock,
      adjustStock,
      checkout,
      addExpense,
      updateExpense,
      deleteExpense,
      addUser,
      updateUser,
      deleteUser,
    }),
    [
      products,
      stockByProductId,
      sales,
      expenses,
      users,
      addProduct,
      syncProductsFromServer,
      updateProduct,
      deleteProduct,
      setStock,
      adjustStock,
      checkout,
      addExpense,
      updateExpense,
      deleteExpense,
      addUser,
      updateUser,
      deleteUser,
    ]
  );

  return (
    <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
  );
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}
